import React, { Component } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core/styles";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";

import KodeWelcome, { shouldShowWelcome } from "./components/KodeWelcome/KodeWelcome";
import { kodeDarkTheme, kodeLightTheme } from "./theme/kodeTheme";
import { setViewerRole, setSecurityEnabled, forgetSecurityEnabled } from "./utility/user-actions";
import AllureDockerSigIn from "./components/AllureDockerSigIn/AllureDockerSigIn";
import AllureDockerMainContainer from "./containers/AllureDockerMainContainer/AllureDockerMainContainer";
import AllureDockerReportFullView from "./components/AllureDockerReportFullView/AllureDockerReportFullView";
import AllureDockerNotFound from "./components/AllureDockerNotFound/AllureDockerNotFound";
import AllureDockerErrorPage from "./components/AllureDockerErrorPage/AllureDockerErrorPage";
import axios from "./api/axios-allure-docker";

class App extends Component {
  state = {
    darkState: true,
    isLogoutNeeded: false,
    isSignInAnOption: false,
    isLoginRequired: null,
    error: null,
    showWelcome: false,
  };

  componentDidMount() {
    this.isLoginRequired();
    this.recoverTheme();
  }

  isAdminEndpointAccessible = () => {
    return axios
      .post("/send-results")
      .then((response) => {
        return response.status !== 401;
      })
      .catch((error) => {
        return error.status !== 401;
      });
  }

  isLoginRequired = () => {
    this.setState({ error: null });
    // Флаг живёт в localStorage и переживает перезагрузку, поэтому до ответа
    // /config он может утверждать, что security выключена, когда её уже
    // включили — и isAdmin() снова пустил бы к админским кнопкам. Забываем
    // его перед запросом: пока ответа нет, доступ закрыт.
    forgetSecurityEnabled();
    axios
      .get("/config")
      .then(async (response) => {
        const isSecurityEnabled = response.data.data.security_enabled;
        setSecurityEnabled(isSecurityEnabled === 1);
        let isLogoutNeeded = false;
        let isSignInAnOption = false;
        if (isSecurityEnabled === 1) {
          isLogoutNeeded = true;
          const isMakeViewerEndpointsPublic = response.data.data.make_viewer_endpoints_public;
          if (isMakeViewerEndpointsPublic === 1 && !await this.isAdminEndpointAccessible()) {
            setViewerRole();
            isLogoutNeeded = false;
            isSignInAnOption = true;
          }
        } else {
          localStorage.removeItem("expirationDate");
          localStorage.removeItem("roles");
        }
        this.setState({
          isLoginRequired: false,
          isLogoutNeeded: isLogoutNeeded,
          isSignInAnOption: isSignInAnOption,
          // Проверяем только когда экран логина уже позади, иначе приветствие
          // накрыло бы форму входа.
          showWelcome: shouldShowWelcome(),
        });
      })
      .catch((error) => {
        if (error.redirect) {
          this.setState({ isLoginRequired: true, isLogoutNeeded: false });
        }
        this.setState({
          error: error,
        });
      });
  };

  // Бренд KODE — чистый чёрный, поэтому дефолт перевёрнут: нет сохранённого
  // выбора => тёмная. Старый ключ darkState читается как фолбэк, пишем уже
  // в kode.theme.
  recoverTheme = () => {
    const storedTheme = localStorage.getItem("kode.theme");
    const legacyDarkState = localStorage.getItem("darkState");
    let darkState = true;
    if (storedTheme !== null) {
      darkState = storedTheme === "dark";
    } else if (legacyDarkState !== null) {
      darkState = legacyDarkState === "true";
    }
    this.applyTheme(darkState);
  };

  handleThemeChange = () => {
    const darkState = !this.state.darkState;
    localStorage.setItem("kode.theme", darkState ? "dark" : "light");
    this.applyTheme(darkState);
  };

  applyTheme = (darkState) => {
    this.setState({ darkState: darkState });
    // Чтобы index.css мог переопределить :root-переменные для не-MUI слоёв.
    document.documentElement.dataset.kodeTheme = darkState ? "dark" : "light";
  };

  render() {
    const theme = this.state.darkState ? kodeDarkTheme : kodeLightTheme;

    let switchRouter = null;
    if (this.state.error) {
      switchRouter = <AllureDockerErrorPage error={this.state.error} />;
    }

    if (this.state.isLoginRequired !== null) {
      if (this.state.isLoginRequired) {
        switchRouter = (
          <Switch>
            <Route
              path="/signin"
              render={() => (
                <AllureDockerSigIn isLoginRequired={this.isLoginRequired}/>
              )}
            />
            <Route render={() => <Redirect to="/signin" />} />
          </Switch>
        );
      } else {
        let signInAnOption;
        if (this.state.isSignInAnOption) {
          signInAnOption = (
            <React.Fragment>
              <Route
                path="/signin"
                render={() => (
                  <AllureDockerSigIn isLoginRequired={this.isLoginRequired} isHomeAnOption={!this.isSignInAnOption}/>
                )}
              />
            </React.Fragment>
          )
        } else {
          signInAnOption = (<Route path="/signin" exact render={() => <Redirect to="/" />} />)
        }

        switchRouter = (
          <Switch>
            <Route
              path="/"
              exact
              render={() => (
                <AllureDockerMainContainer
                  darkState={this.state.darkState}
                  handleThemeChange={this.handleThemeChange}
                  isLogoutNeeded={this.state.isLogoutNeeded}
                  isSignInAnOption={this.state.isSignInAnOption}
                />
              )}
            />
            {/* :id+ матчит вложенные id со слэшами (kode/mobile/android).
                Роут отчёта обязан идти ПЕРЕД детальным, иначе :id+ его поглотит. */}
            <Route
              path="/projects/:id+/reports/:reportId(latest|[0-9]+)"
              exact
              render={() => <AllureDockerReportFullView />}
            />
            <Route
              path="/projects/:id+"
              exact
              render={() => (
                <AllureDockerMainContainer
                  darkState={this.state.darkState}
                  handleThemeChange={this.handleThemeChange}
                  isLogoutNeeded={this.state.isLogoutNeeded}
                  isSignInAnOption={this.state.isSignInAnOption}
                />
              )}
            />
            {signInAnOption}
            <Route component={AllureDockerNotFound} />
          </Switch>
        );
      }
    }
    return (
      <ThemeProvider theme={theme}>
        {/* Единственный CssBaseline в приложении: MuiCssBaseline.@global —
            глобальный JSS-лист, и пять монтирований давали пять копий,
            порядок которых зависел от порядка монтирования роутов. */}
        <CssBaseline />
        {this.state.showWelcome && (
          <KodeWelcome onDone={() => this.setState({ showWelcome: false })} />
        )}
        <BrowserRouter basename={window._env_.ROUTER_BASE_NAME}>
          {switchRouter}
        </BrowserRouter>
      </ThemeProvider>
    );
  }
}

export default App;
